/**
 * losi-steps-merge
 *
 * Shared normalization and monotonic merge logic for streamed Losi steps.
 *
 * Primary responsibilities:
 * - Parse losi_steps annotation payloads into typed steps
 * - Prevent stale streamed annotations from regressing step status
 *
 * Dependencies:
 * - ChatTodoItem types
 */

import type { ChatTodoItem, ChatTodoStatus } from '@/db/chat-todos'
import type { Json } from '@/supabase/types'

const LOSI_STEP_STATUS_RANK: Record<ChatTodoStatus, number> = {
  pending: 0,
  in_progress: 1,
  done: 2
}

// --------------

function isLosiStepStatus(value: unknown): value is ChatTodoStatus {
  return value === 'pending' || value === 'in_progress' || value === 'done'
}

// --------------

function parseLosiStep(value: unknown, fallbackOrder: number): ChatTodoItem | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  const record = value as Record<string, unknown>
  const id = typeof record.id === 'string' ? record.id : ''
  const label = typeof record.label === 'string' ? record.label : ''
  if (!id || !label) return null

  return {
    id,
    label,
    order: typeof record.order === 'number' ? record.order : fallbackOrder,
    status: isLosiStepStatus(record.status) ? record.status : 'pending'
  }
}

// --------------

export function normalizeLosiSteps(value: unknown): ChatTodoItem[] {
  if (!Array.isArray(value)) return []

  return value.flatMap((item, index) => {
    const step = parseLosiStep(item, index)
    return step ? [step] : []
  })
}

// --------------

export function mergeLosiSteps(current: unknown, next: unknown): Json {
  if (!Array.isArray(next)) {
    return next as Json
  }

  const currentById = new Map<string, ChatTodoItem>()
  for (const step of normalizeLosiSteps(current)) {
    currentById.set(step.id, step)
  }

  return next.map((item, index) => {
    const nextStep = parseLosiStep(item, index)
    if (!nextStep) return item as Json

    const currentStep = currentById.get(nextStep.id)
    if (
      currentStep &&
      LOSI_STEP_STATUS_RANK[currentStep.status] > LOSI_STEP_STATUS_RANK[nextStep.status]
    ) {
      return {
        ...nextStep,
        status: currentStep.status
      } satisfies ChatTodoItem
    }

    return nextStep
  }) as Json
}
